import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import { Sticky } from 'react-sticky';

import { Box, Flex, Grid } from '../UIKit/system';
import Container from '../container';
import Logo from '../../images/svg/logo.svg';
import NavItem from './nav-item';
import LogoLink from '../logo-link';
import mq from '../../utils/mq';
import { getThemeColor } from '../../utils/styles';
import Burger from '../burger';
import Button from '../button';
import MobileNavigation from './mobile-navigation';
import TopBar from './top-bar';
import { withResponsiveContext } from '../responsive';
import { useNavigation } from '../../hooks/use-navigation';
import { useMobileNavigation } from '../../hooks/use-mobile-navigation';
import { getPath } from '../../utils/paths';
import { SearchButton } from '../global-search';
import { useSearchSetState, useSearchState } from '../../context/search';
import Icon from '../icon';

const StyledNavigation = styled.nav`
  width: 100%;
  max-width: 100vw;
  position: sticky;
  top: 0;
  z-index: 125;
  user-select: none;

  ${mq.tablet(css`
    margin-top: 0;
  `)};
`;

const TopMobileBar = styled(Grid).attrs(() => ({
  alignItems: 'center',
  gridAutoFlow: 'column',
  justifyContent: 'space-between',
  gridGap: 20
}))``;

const Nav = styled(Grid).attrs({ as: 'nav' })`
  > :first-child {
    display: grid;

    ${mq.tablet(css`
      display: none;
    `)}
  }

  > :last-child {
    display: none;

    ${mq.tablet(css`
      display: grid;
    `)}
  }
`;

const NavBar = styled(Box)`
  z-index: 120;
  width: 100%;
`;

const NavBarInner = styled(Box)`
  border-bottom-width: 0px;
  ${mq.tablet(css`
    background-color: ${getThemeColor('black')};
    margin-top: 0;
  `)};
`;

const StyledLogo = styled(Logo)`
  fill: #000;

  ${mq.tablet(css`
    fill: #fff;
  `)};

  ${mq.mobile(css`
    position: relative;
    top: 1px;
    width: 54px;
  `)};
`;

const Menu = styled(Grid)`
  align-items: center;
  gap: 20px;
  grid-template-columns: 1fr auto 1fr;
  justify-content: space-between;

  ${mq.tablet(css`
    display: none;
  `)};
`;

const StyledNavItem = styled(NavItem)`
  align-content: center;
`;

const ButtonContact = styled(Button)`
  justify-self: end;
`;

const StyledBurger = styled.button`
  background: none;
  border: none;
  justify-self: start;
`;

const MobileCloseSearch = styled.button.attrs(() => ({
  type: 'button',
  children: <Icon name="cross" fillColor="white" />
}))`
  background: transparent;
  border: none;

  svg {
    ${mq.tablet(css`
      height: 16px;
      width: 22px;
    `)}

    ${mq.mobile(css`
      height: 16px;
      width: 16px;
    `)}
  }
`;

const NavigationInnerComponent = React.forwardRef(
  (
    {
      navbarStyles,
      items_left,
      items_right,
      editorContent,
      showMobileMenu,
      setShowMobileMenu,
      searchData
    },
    ref
  ) => {
    const [currentNavItemHover, setCurrentNavItemHover] = useState('');
    const searchOpen = useSearchState();
    const searchSetState = useSearchSetState();

    function renderNavItem(key, item) {
      return (
        <StyledNavItem
          key={key}
          isEditorContent={!!editorContent}
          dropdownIsVisible={currentNavItemHover === item.children}
          onSetDropdownName={(name) => setCurrentNavItemHover(name)}
          {...item}
        />
      );
    }

    return (
      <NavBar
        position={'relative'}
        style={navbarStyles}
        ref={ref}
        onMouseLeave={() => setCurrentNavItemHover('')}
      >
        <NavBarInner
          position={'relative'}
          zIndex={2}
          borderBottomWidth={[0, 0, 1]}
          borderBottomStyle={'solid'}
          borderBottomColor={'#ddd'}
          bg={'#fff'}
        >
          <Container large>
            <Nav height={[50, 80, 88]}>
              <Menu>
                <Flex>
                  {items_left.map((item, index) => {
                    return renderNavItem(index, item);
                  })}
                </Flex>
                <LogoLink>
                  <StyledLogo />
                </LogoLink>
                <Flex justifyContent="end" gridGap={28}>
                  {items_right.map((item, index) => {
                    return renderNavItem(index, {
                      ...item,
                      marginGap: false
                    });
                  })}
                  <SearchButton
                    show={!!searchData}
                    onOpen={() => setCurrentNavItemHover('')}
                  />
                  <ButtonContact to={getPath.contact()} navigationCtaButton>
                    Get a Quote
                  </ButtonContact>
                </Flex>
              </Menu>
              <TopMobileBar>
                <Grid gridAutoFlow="column" gridGap={20}>
                  {searchOpen ? (
                    <MobileCloseSearch onClick={() => searchSetState(false)} />
                  ) : (
                    <StyledBurger
                      aria-label={
                        showMobileMenu
                          ? 'Close navigation menu'
                          : 'Open navigation menu'
                      }
                      onClick={() => setShowMobileMenu(true)}
                    >
                      <Burger />
                    </StyledBurger>
                  )}

                  <LogoLink>
                    <StyledLogo />
                  </LogoLink>
                </Grid>

                <Grid gridAutoFlow="column" gridGap={20}>
                  <SearchButton show={!!searchData} />
                  <ButtonContact to={getPath.contact()} navigationCtaButton>
                    Get a Quote
                  </ButtonContact>
                </Grid>
              </TopMobileBar>
            </Nav>
          </Container>
        </NavBarInner>
        <div id="nav-modal-container" />
      </NavBar>
    );
  }
);

NavigationInnerComponent.displayName = 'NavigationInnerComponent';

const Navigation = ({ isMobileOrTablet, editorContent, searchData }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const cmsContent = useNavigation();
  const mobileNavigationData = useMobileNavigation();

  const { items_top_bar, items_left, items_right } =
    editorContent || cmsContent;

  function handleCloseMenu() {
    setShowMobileMenu(false);
  }

  useEffect(() => {
    if (!isMobileOrTablet) {
      setShowMobileMenu(false);
    }
  }, [isMobileOrTablet]);

  return (
    <React.Fragment>
      <TopBar content={items_top_bar[0]} />
      <StyledNavigation>
        <MobileNavigation
          show={showMobileMenu}
          onClose={handleCloseMenu}
          data={mobileNavigationData}
        />
        <Sticky
          topOffset={isMobileOrTablet ? 0 : 50}
          disableHardwareAcceleration
        >
          {({ style: stickyStyles }) => {
            return (
              <NavigationInnerComponent
                navbarStyles={stickyStyles}
                items_top_bar={items_top_bar}
                items_left={items_left}
                items_right={items_right}
                editorContent={editorContent}
                showMobileMenu={showMobileMenu}
                setShowMobileMenu={setShowMobileMenu}
                searchData={searchData}
              />
            );
          }}
        </Sticky>
      </StyledNavigation>
    </React.Fragment>
  );
};

export default withResponsiveContext(Navigation);
